import React from 'react';
import { Button } from 'antd';
import { 
  DownloadOutlined,
  FileOutlined, 
  FilePdfOutlined, 
  FileWordOutlined, 
  FileExcelOutlined, 
  FilePptOutlined,
  FileZipOutlined,
  FileTextOutlined,
  FileMarkdownOutlined
} from '@ant-design/icons';
import './UnsupportedFileView.css';

/**
 * Get appropriate icon for file type based on MIME type or filename
 */
const getFileIcon = (mimeType, filename) => {
  const iconStyle = { fontSize: 64 };
  
  // Check MIME type first
  if (mimeType) {
    if (mimeType.includes('pdf')) {
      return <FilePdfOutlined style={iconStyle} />;
    }
    if (mimeType.includes('word') || mimeType.includes('document')) {
      return <FileWordOutlined style={iconStyle} />;
    }
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return <FileExcelOutlined style={iconStyle} />;
    }
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
      return <FilePptOutlined style={iconStyle} />;
    }
    if (mimeType.includes('zip') || mimeType.includes('compressed') || mimeType.includes('archive')) {
      return <FileZipOutlined style={iconStyle} />;
    }
    if (mimeType.includes('text/plain')) {
      return <FileTextOutlined style={iconStyle} />;
    }
    if (mimeType.includes('markdown')) {
      return <FileMarkdownOutlined style={iconStyle} />;
    }
  }
  
  // Fallback to extension-based detection
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FilePdfOutlined style={iconStyle} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={iconStyle} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined style={iconStyle} />;
      case 'ppt':
      case 'pptx':
        return <FilePptOutlined style={iconStyle} />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FileZipOutlined style={iconStyle} />;
      case 'txt':
        return <FileTextOutlined style={iconStyle} />;
      case 'md':
        return <FileMarkdownOutlined style={iconStyle} />;
      default:
        return <FileOutlined style={iconStyle} />;
    }
  }
  
  return <FileOutlined style={iconStyle} />;
};

/**
 * Get human-readable file type label
 */
const getFileTypeLabel = (mimeType, filename) => {
  // Check MIME type first
  if (mimeType) {
    if (mimeType.includes('pdf')) return 'PDF Document';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Microsoft Word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Microsoft Excel';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Microsoft PowerPoint';
    if (mimeType.includes('zip')) return 'ZIP Archive';
    if (mimeType.includes('rar')) return 'RAR Archive';
    if (mimeType.includes('7z')) return '7-Zip Archive';
    if (mimeType.includes('tar')) return 'TAR Archive';
    if (mimeType.includes('gzip') || mimeType.includes('gz')) return 'GZIP Archive';
    if (mimeType.includes('text/plain')) return 'Text File';
    if (mimeType.includes('markdown')) return 'Markdown';
  }
  
  // Fallback to extension
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'PDF Document';
      case 'doc': return 'Microsoft Word (Legacy)';
      case 'docx': return 'Microsoft Word';
      case 'xls': return 'Microsoft Excel (Legacy)';
      case 'xlsx': return 'Microsoft Excel';
      case 'ppt': return 'Microsoft PowerPoint (Legacy)';
      case 'pptx': return 'Microsoft PowerPoint';
      case 'zip': return 'ZIP Archive';
      case 'rar': return 'RAR Archive';
      case '7z': return '7-Zip Archive';
      case 'tar': return 'TAR Archive';
      case 'gz': return 'GZIP Archive';
      case 'txt': return 'Text File';
      case 'md': return 'Markdown';
      case 'rtf': return 'Rich Text Format';
      case 'odt': return 'OpenDocument Text';
      case 'ods': return 'OpenDocument Spreadsheet';
      case 'odp': return 'OpenDocument Presentation';
      default: return ext ? ext.toUpperCase() + ' File' : 'Unknown File';
    }
  }
  
  return 'Unknown File';
};

/**
 * Format file size to human-readable string
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * UnsupportedFileView - Displays a friendly UI for files that can't be previewed
 */
const UnsupportedFileView = ({ filename, fileSize, mimeType, url, isDarkMode }) => {
  const icon = getFileIcon(mimeType, filename);
  const typeLabel = getFileTypeLabel(mimeType, filename);
  const formattedSize = formatFileSize(fileSize);
  
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Build meta string (type + size if available)
  const metaParts = [typeLabel];
  if (formattedSize) {
    metaParts.push(formattedSize);
  }
  const metaString = metaParts.join(' • ');
  
  return (
    <div className={`unsupported-file-view ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="unsupported-file-icon">
        {icon}
      </div>
      
      <h3 className="unsupported-file-name">
        {filename || 'Unknown File'}
      </h3>
      
      <p className="unsupported-file-meta">
        {metaString}
      </p>
      
      <p className="unsupported-file-message">
        Preview not available for this file type.<br />
        Download to view on your computer.
      </p>
      
      <Button 
        type="primary" 
        icon={<DownloadOutlined />}
        size="large"
        onClick={handleDownload}
        className="unsupported-file-download-btn"
      >
        Download
      </Button>
    </div>
  );
};

export default UnsupportedFileView;
